/**
 * Buy with Prime Handler
 * Manages visibility of Buy with Prime button based on selling plan selection.
 * Hides the button when a selling_plan parameter is present in the URL,
 * since Buy with Prime doesn't support subscription checkout.
 */
class BuyWithPrimeHandler {
  constructor() {
    this.styleId = 'buy-with-prime-hide-style';
    this.observer = null;
    this.buttonSelector = 'delivery-promise-wc[slot="promise"]';

    // Initialize the handler
    this.init();
  }

  init() {
    // console.log('[BuyWithPrime] Initializing handler');

    // Set up event listeners for variant changes
    this.setupVariantListeners();

    // Set up mutation observer to enforce hiding
    this.setupMutationObserver();

    // Check initial state on page load
    this.updatePrimeButtonVisibility();
  }

  /**
   * Set up mutation observer to enforce button hiding
   */
  setupMutationObserver() {
    // Check if MutationObserver is supported
    if (typeof MutationObserver === 'undefined') {
      // Fallback: check periodically
      this.fallbackInterval = setInterval(() => {
        if (this.hasSellingPlanInUrl()) {
          this.enforceButtonHiding();
        }
      }, 1000);
      return;
    }

    try {
      // Create observer to watch for Buy with Prime button changes
      this.observer = new MutationObserver(() => {
        // If we should be hiding the button, enforce it
        if (this.hasSellingPlanInUrl()) {
          this.enforceButtonHiding();
        }
      });

      // Find the specific container to observe instead of entire body
      const productForm = document.querySelector('.product-form') ||
                          document.querySelector('[data-product-form]') ||
                          document.querySelector('.product__info');

      const targetNode = productForm || document.body;

      // Start observing with more limited scope if possible
      this.observer.observe(targetNode, {
        childList: true,
        subtree: productForm ? true : false, // Only use subtree if we have a specific container
        attributes: true,
        attributeFilter: ['style', 'class']
      });
    } catch (e) {
      // Fallback if MutationObserver fails
      this.fallbackInterval = setInterval(() => {
        if (this.hasSellingPlanInUrl()) {
          this.enforceButtonHiding();
        }
      }, 1000);
    }
  }

  /**
   * Set up listeners for variant selection events
   */
  setupVariantListeners() {
    // Store bound handlers for cleanup
    this.variantSelectedHandler = () => {
      // console.log('[BuyWithPrime] Variant selected event');
      // Small delay to let URL update
      if (this.variantTimeout) clearTimeout(this.variantTimeout);
      this.variantTimeout = setTimeout(() => this.updatePrimeButtonVisibility(), 100);
    };

    this.variantUpdateHandler = () => {
      // console.log('[BuyWithPrime] Variant update event');
      if (this.updateTimeout) clearTimeout(this.updateTimeout);
      this.updateTimeout = setTimeout(() => this.updatePrimeButtonVisibility(), 100);
    };

    // Listen for variant events
    document.addEventListener('variant:selected', this.variantSelectedHandler);
    document.addEventListener('variant:update', this.variantUpdateHandler);

    // Store original history methods for restoration
    this.originalPushState = history.pushState;
    this.originalReplaceState = history.replaceState;

    // Listen for URL changes (for history.pushState/replaceState)
    const self = this;
    history.pushState = function(...args) {
      self.originalPushState.apply(this, args);
      setTimeout(() => window.buyWithPrimeHandler?.updatePrimeButtonVisibility(), 100);
    };

    history.replaceState = function(...args) {
      self.originalReplaceState.apply(this, args);
      if (self.historyTimeout) clearTimeout(self.historyTimeout);
      self.historyTimeout = setTimeout(() => window.buyWithPrimeHandler?.updatePrimeButtonVisibility(), 100);
    };
  }

  /**
   * Check if a selling plan is present in the URL
   * @returns {boolean} True if selling_plan parameter exists
   */
  hasSellingPlanInUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('selling_plan');
  }

  /**
   * Update the visibility of the Buy with Prime button
   */
  updatePrimeButtonVisibility() {
    const hasSellingPlan = this.hasSellingPlanInUrl();

    // console.log('[BuyWithPrime] Checking URL - has selling_plan:', hasSellingPlan);
    // console.log('[BuyWithPrime] Current URL:', window.location.href);

    if (hasSellingPlan) {
      // console.log('[BuyWithPrime] Hiding Buy with Prime button (selling_plan in URL)');
      this.injectHideStyle();
      this.enforceButtonHiding();
    } else {
      // console.log('[BuyWithPrime] Showing Buy with Prime button');
      this.removeHideStyle();
    }
  }

  /**
   * Inject CSS to hide the Buy with Prime button
   */
  injectHideStyle() {
    // Check if style already exists
    if (document.getElementById(this.styleId)) {
      return;
    }

    // Create style element
    const style = document.createElement('style');
    style.id = this.styleId;
    style.textContent = `
      delivery-promise-wc[slot="promise"] {
        display: none !important;
      }
    `;

    // Inject into head
    document.head.appendChild(style);
    // console.log('[BuyWithPrime] Hide style injected');
  }

  /**
   * Remove the hide style and clear inline styles
   */
  removeHideStyle() {
    const style = document.getElementById(this.styleId);
    if (style) {
      style.remove();
      // console.log('[BuyWithPrime] Hide style removed');
    }

    // Also remove inline styles from buttons
    const buttons = document.querySelectorAll(this.buttonSelector);
    buttons.forEach((button) => {
      if (button.style.display === 'none') {
        button.style.removeProperty('display');
        // console.log('[BuyWithPrime] Removed inline hiding from button');
      }
    });
  }

  /**
   * Enforce button hiding by directly setting inline styles
   */
  enforceButtonHiding() {
    const buttons = document.querySelectorAll(this.buttonSelector);
    buttons.forEach((button) => {
      if (button.style.display !== 'none') {
        button.style.setProperty('display', 'none', 'important');
        // console.log('[BuyWithPrime] Enforced hiding on button');
      }
    });
  }

  /**
   * Clean up the handler
   */
  destroy() {
    // console.log('[BuyWithPrime] Destroying handler');

    // Disconnect mutation observer
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    // Clear fallback interval if it exists
    if (this.fallbackInterval) {
      clearInterval(this.fallbackInterval);
      this.fallbackInterval = null;
    }

    // Clear any pending timeouts
    if (this.variantTimeout) clearTimeout(this.variantTimeout);
    if (this.updateTimeout) clearTimeout(this.updateTimeout);
    if (this.historyTimeout) clearTimeout(this.historyTimeout);

    // Remove event listeners
    if (this.variantSelectedHandler) {
      document.removeEventListener('variant:selected', this.variantSelectedHandler);
    }
    if (this.variantUpdateHandler) {
      document.removeEventListener('variant:update', this.variantUpdateHandler);
    }

    // Restore original history methods
    if (this.originalPushState) {
      history.pushState = this.originalPushState;
    }
    if (this.originalReplaceState) {
      history.replaceState = this.originalReplaceState;
    }

    this.removeHideStyle();
  }
}

// Initialize the handler when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.buyWithPrimeHandler = new BuyWithPrimeHandler();
  });
} else {
  // DOM is already loaded
  window.buyWithPrimeHandler = new BuyWithPrimeHandler();
}

// Export for ES module compatibility
if (typeof module !== 'undefined' && module.exports) {
  module.exports = BuyWithPrimeHandler;
}
